import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { motion } from 'framer-motion';

import Wrapper from '../../elements/wrapper';
import X from '../../../images/icons/x.inline.svg';
import SearchIcon from '../../../images/icons/search.inline.svg';
import { media } from '../../helpers';

const ColorModalContainer = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    z-index: 6000;
    background-color: #f2f0eb;

    .close-container {
        text-align: right;
        position: absolute;
        top: 0;
        right: 0;
        button {
            border: none;
            background-color: transparent;
            padding: 20px;
            svg {
                height: 35px;
                width: 35px;
            }
        }
    }

    .title-container {
        padding-top: 15vh;
        text-align: center;
        h4 {
            font-size: 2.5rem;
            color: #000;
            font-family: neue-kabel, sans-serif;
            font-weight: 800;
            margin: 0 0 30px;
        }
    }

    .tabs-container {
        display: flex;
        justify-content: center;
        ${media.medium`max-width: 50vw;`}

        .btn-container {
            flex: 1;
            display: flex;
            &:first-child {
                margin-right: 20px;
            }

            button {
                flex: 1;
                padding: 30px;
                background-color: #fff;
                text-align: left;
                border: 1px solid #fff;

                &:active,
                &:focus {
                    outline: none;
                }
                h4 {
                    margin: 0 0 30px;
                }

                p {
                    margin: 0;
                }

                &.active {
                    background-color: #f74d33;

                    p,
                    h4 {
                        color: #fff;
                    }
                }
            }
        }
    }

    .all-colors-wrapper {
        background-color: #fff;
        max-width: 100vw;
        padding: 40px;

        .color-filter-wrapper {
            display: flex;
            align-items: center;
            padding-bottom: 4vh;
            ${media.large`max-width: 88.5vw;`}

            .form-container {
                flex: 3;

                form {
                    display: flex;
                    margin-bottom: 0px;
                    position: relative;
                    input {
                        flex: 1;
                        padding: 10px 0 10px 40px;
                        background-color: #f2f0eb;
                        border: 1px solid #ccc;
                        border-radius: 5px;
                    }

                    span {
                        position: absolute;
                        top: 11px;
                        left: 10px;
                    }
                }
            }

            .color-family-container {
                select {
                    text-transform: capitalize;
                    margin-left: 20px;
                }
            }
        }

        .grid-wrapper {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            max-height: 50vh;
            overflow-y: scroll;
            padding-bottom: 100px;
            .color-container {
                margin-bottom: 20px;
                text-align: center;
                flex: 0 0 50%;
                ${media.small`flex: 0 0 33.33%;`}
                ${media.medium`flex: 0 0 20%;`}
                ${media.laptop`flex: 0 0 16.666%;`}
                button {
                    width: 100%;
                    max-width: 90%;
                    margin: 0 auto;
                    padding: 0;
                    border: none;
                    background-color: transparent;
                    box-shadow: 5px 5px 12px rgba(0, 0, 0, 0.25);

                    .color-swatch {
                        height: 100px;
                        width: 100%;
                        display: block;
                    }

                    .content-container {
                        h4 {
                            margin: 0;
                            padding: 15px 20px;
                            text-align: left;
                            font-weight: 700;
                            font-size: 0.9rem;
                        }
                    }
                }
            }
        }
    }
`;

const ColorModal = ({ toggleModalClose, addColor }) => {
    const data = useStaticQuery(graphql`
        query {
            allGoogleSheetPantoneRow {
                edges {
                    node {
                        rgbcode
                        colorname
                    }
                }
            }

            allGoogleSheetGoodformColorsRow {
                edges {
                    node {
                        rgbcode
                        colorname
                        colorfamily
                    }
                }
            }
        }
    `);

    const [tabActive, setTabActive] = useState(0);

    let allPantoneColors = data && data.allGoogleSheetPantoneRow.edges;
    let allGoodformColors = data && data.allGoogleSheetGoodformColorsRow.edges;

    const [currentColors, setCurrentColors] = useState([]);
    const [colorFamilies, setColorFamilies] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    function handleTabChange(index) {
        setTabActive(index);
        setSearchValue('');
        setCurrentColors(index === 0 ? allPantoneColors : allGoodformColors);
    }

    function handleColorFilter(e) {
        let colorFamily = e.target.value;
        let filteredColors = [];
        if (colorFamily !== 'all') {
            filteredColors = allGoodformColors.filter(
                (color) => color.node.colorfamily === colorFamily
            );
        } else {
            filteredColors = allGoodformColors;
        }
        setCurrentColors(filteredColors);
        setSearchValue('');
    }

    function handleSearch(e) {
        setSearchValue(e.target.value);
        let colorsToFilter =
            tabActive === 0 ? allPantoneColors : allGoodformColors;
        if (e.target.value !== '') {
            let filteredColors = colorsToFilter?.filter(
                (colorNode) =>
                    colorNode.node.colorname &&
                    colorNode.node.colorname
                        .toLowerCase()
                        .includes(searchValue.toLowerCase())
            );
            setCurrentColors(filteredColors);
        } else {
            setCurrentColors(colorsToFilter);
        }
    }

    function getAllColorFamilies() {
        let filteredColorFamilies = [];

        allGoodformColors.map((colorNode) => {
            let color = colorNode.node;
            if (filteredColorFamilies.indexOf(color.colorfamily) === -1) {
                filteredColorFamilies.push(color.colorfamily);
            }
        });

        setColorFamilies(filteredColorFamilies);
    }

    useEffect(() => {
        getAllColorFamilies();
        setCurrentColors(allPantoneColors);
    }, []);

    return (
        <ColorModalContainer
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
                open: { transform: 'translate3d(0, 0%, 0)' },
                collapsed: { transform: 'translate3d(0, 100%, 0)' },
            }}
            transition={{
                duration: 0.5,
            }}
        >
            <div className="close-container">
                <button onClick={() => toggleModalClose()}>
                    <X />
                </button>
            </div>
            <Wrapper className="modal-title-container">
                <div className="select-method-container">
                    <div className="title-container">
                        <h4>Which colors would you like to use?</h4>
                    </div>
                </div>
                <Wrapper className="tabs-container">
                    <div className="btn-container">
                        <button
                            onClick={() => handleTabChange(0)}
                            className={tabActive === 0 ? 'active' : ''}
                        >
                            <h4>Pantone</h4>
                            <p>
                                Choose a coated or uncoated color from a PMS
                                number
                            </p>
                        </button>
                    </div>
                    <div className="btn-container">
                        <button
                            onClick={() => handleTabChange(1)}
                            className={tabActive === 1 ? 'active' : ''}
                        >
                            <h4>Goodform Colors</h4>
                            <p>
                                Choose or search for 500+ select standard
                                Goodform Colors.
                            </p>
                        </button>
                    </div>
                </Wrapper>
            </Wrapper>
            <Wrapper className="all-colors-wrapper">
                <Wrapper className="color-filter-wrapper">
                    <div className="form-container">
                        <form>
                            <input
                                type="text"
                                value={searchValue}
                                onChange={(e) => handleSearch(e)}
                            />
                            <span>
                                <SearchIcon />
                            </span>
                        </form>
                    </div>
                    {tabActive === 1 && (
                        <div className="color-family-container">
                            <select
                                name=""
                                id=""
                                onChange={(e) => handleColorFilter(e)}
                            >
                                <option value="all">All Color Families</option>
                                {colorFamilies &&
                                    colorFamilies.map((family, index) => {
                                        return (
                                            <option key={index} value={family}>
                                                {family}
                                            </option>
                                        );
                                    })}
                            </select>
                        </div>
                    )}
                </Wrapper>

                <Wrapper className="grid-wrapper pantone">
                    {currentColors &&
                        currentColors.map((colorNode, index) => {
                            let color = colorNode.node;
                            return (
                                <div className="color-container" key={index}>
                                    <button
                                        onClick={() =>
                                            addColor(
                                                color.colorname,
                                                color.rgbcode
                                            )
                                        }
                                        className="inner-wrap"
                                    >
                                        <span
                                            className="color-swatch"
                                            style={{
                                                backgroundColor: color.rgbcode,
                                            }}
                                        ></span>
                                        <span className="content-container">
                                            <h4>{color.colorname}</h4>
                                        </span>
                                    </button>
                                </div>
                            );
                        })}
                </Wrapper>
            </Wrapper>
        </ColorModalContainer>
    );
};

export default ColorModal;
